
import { Card } from "@/components/ui/card";
import { CalendarDays, History, Plus, Eye, MessageSquare, Repeat, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { isAfter, isBefore, endOfDay, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { formatDateAndTime } from "@/utils/formatDate";
import { Badge } from "@/components/ui/badge";
import { OrderStatus, ORDER_STATUS_COLORS, ORDER_STATUS_LABELS } from "@/utils/orderStatus";

const Meals = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const {
    data: orders,
    isLoading
  } = useQuery({
    queryKey: ['user-meals'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          meal:meals!orders_meal_id_fkey (
            *,
            chef:profiles!meals_chef_id_fkey (
              first_name,
              last_name
            )
          )
        `)
        .eq('foodie_id', user.id)
        .in('status', ['in_progress', 'ready', 'completed'])
        .order('scheduled_date', { ascending: true });
        
      if (error) throw error;
      console.log('Orders fetched:', data?.length || 0);
      return data || [];
    }
  });

  const currentTime = new Date();
  
  const isUpcomingMeal = (order: any) => {
    // Orders that are 'completed' are always in "Previous Meals"
    if (order.status === 'completed') return false;
    
    // Orders that are 'in_progress' or 'ready' are in "Upcoming Meals"
    if (order.status === 'in_progress' || order.status === 'ready') {
      if (!order.scheduled_date) return true; // If no date, consider it upcoming
      
      // Check if the scheduled date is in the future
      const scheduledDate = parseISO(order.scheduled_date);
      return isAfter(scheduledDate, currentTime);
    }
    
    // Default fallback - if status is something else, check the date
    if (!order.scheduled_date) return false;
    const scheduledDate = parseISO(order.scheduled_date);
    return isAfter(scheduledDate, currentTime);
  };
  
  const upcomingMeals = orders?.filter(isUpcomingMeal) || [];
  const previousMeals = orders?.filter(order => !isUpcomingMeal(order)) || [];
  
  console.log('Upcoming meals:', upcomingMeals.length);
  console.log('Previous meals:', previousMeals.length);
  
  const handleMessageChef = async (chefId: string, mealId: string) => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      const {
        data: conversation,
        error
      } = await supabase.from('conversations').insert({
        foodie_id: user.id,
        cook_id: chefId,
        meal_id: mealId
      }).select().single();
      if (error) {
        console.error('Error creating conversation:', error);
        toast({
          title: "Error",
          description: "Failed to start conversation. Please try again.",
          variant: "destructive"
        });
        return;
      }
      navigate('/inbox', {
        state: {
          selectedConversation: conversation.id,
          transition: 'slide'
        }
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  if (isLoading) {
    return <div className="min-h-screen bg-background pb-16">
        <main className="container mx-auto px-[16px] py-[16px]">
          <div className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-32 w-full" />
          </div>
          <div className="space-y-4 mt-6">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-32 w-full" />
          </div>
        </main>
      </div>;
  }
  
  return (
    <div className="min-h-screen bg-background pb-16">
      <main className="container mx-auto px-[16px] py-[16px] max-w-7xl">
        <section>
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Upcoming Meals</h2>
            </div>
            <Button asChild>
              <Link to="/">
                <Plus className="h-4 w-4 mr-2" />
                Add Meal
              </Link>
            </Button>
          </div>
          
          {upcomingMeals.length === 0 ? (
            <p className="text-muted-foreground">No upcoming meals scheduled</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingMeals.map(order => (
                <Card key={order.id} className="overflow-hidden flex flex-col">
                  <div className="aspect-[16/9] relative">
                    <img 
                      src={order.meal.image || "https://via.placeholder.com/400x300"} 
                      alt={order.meal.name} 
                      className="absolute inset-0 w-full h-full object-cover" 
                    />
                    <Badge 
                      className={`absolute top-2 right-2 ${ORDER_STATUS_COLORS[order.status as OrderStatus]}`}
                    >
                      {ORDER_STATUS_LABELS[order.status as OrderStatus]}
                    </Badge>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg line-clamp-2 mb-2">{order.meal.name}</h3>
                      <p className="text-muted-foreground mb-1">
                        Chef {order.meal.chef?.first_name} {order.meal.chef?.last_name}
                      </p>
                      <p className="text-muted-foreground">
                        {formatDateAndTime(order.scheduled_date, order.scheduled_time)}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mt-4">
                      <Button variant="outline" asChild className="flex-1">
                        <Link to={`/meal/${order.meal.id}`}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Listing
                        </Link>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex-1" 
                        onClick={() => handleMessageChef(order.meal.chef_id, order.meal.id)}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Message Chef
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">Previous Meals</h2>
          </div>
          
          {previousMeals.length === 0 ? (
            <p className="text-muted-foreground">No previous meals</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {previousMeals.map(order => (
                <Card key={order.id} className="overflow-hidden flex flex-col">
                  <div className="aspect-[16/9] relative">
                    <img 
                      src={order.meal.image || "https://via.placeholder.com/400x300"} 
                      alt={order.meal.name} 
                      className="absolute inset-0 w-full h-full object-cover" 
                    />
                    <Badge 
                      className={`absolute top-2 right-2 ${ORDER_STATUS_COLORS[order.status as OrderStatus]}`}
                    >
                      {ORDER_STATUS_LABELS[order.status as OrderStatus]}
                    </Badge>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg line-clamp-2 mb-2">{order.meal.name}</h3>
                      <p className="text-muted-foreground mb-1">
                        Chef {order.meal.chef?.first_name} {order.meal.chef?.last_name}
                      </p>
                      <p className="text-muted-foreground">
                        {formatDateAndTime(order.scheduled_date, order.scheduled_time)}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mt-4">
                      <Button variant="outline" asChild className="flex-1">
                        <Link to={`/meal/${order.meal.id}`}>
                          <Repeat className="h-4 w-4 mr-2" />
                          Order Again
                        </Link>
                      </Button>
                      <Button variant="outline" asChild className="flex-1">
                        <Link to={`/review/${order.id}`}>
                          <Star className="h-4 w-4 mr-2" />
                          Leave Review
                        </Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Meals;
